import React from "react";
import { formatDateTimeInTZ, formatTimeInTZ } from "../../lib/timezone";
import Tooltip from "../ui/Tooltip";
import { getOwnerInitials, getOwnerColor } from "../../utils/ownerBadge";

// =============================================================================
// CHAT BUBBLE COMPONENT — Unified
// =============================================================================
// PURPOSE: Instagram-style chat bubble for DM conversations.
// Used by DMSampleModal (sample conversations) and ConversationViewModal (full threads).
//
// FEATURES:
// - Lead messages: left-aligned, glass background, rounded-bl-none
// - Setter messages: right-aligned, blue background, rounded-br-none
// - System messages: centered, gray italic text
// - Highlight/anchor ring for scroll-to-message (proof modals)
// - Handles null text with "[Image sent]" or "[Story reaction]" fallback
// - Timezone-aware timestamps
// - forwardRef for scroll-to-highlight
// =============================================================================

export interface ChatBubbleMessage {
  text?: string | null;
  sender: string; // "lead" | "setter" | "system"
  sent_at: string;
  has_media?: boolean;
}

// Legacy type alias — used by DMSampleModal
export type SampleMessage = ChatBubbleMessage;

interface ChatBubbleProps {
  message: ChatBubbleMessage;
  timezone: string;
  highlightType?: "highlight" | "anchor";
  /** Dim this bubble (outside active date range) */
  dimmed?: boolean;
  /**
   * owner_at_send resolved to a display name — who owned the lead when this
   * message was sent (fired-setter ownership, STEP 0). Renders a letter badge
   * on outbound setter bubbles. `undefined` = feature off (no badge); `null` =
   * feature on but owner unknown (muted "?" badge). Only owner-aware callers
   * pass it, so DMSampleModal etc. are unaffected.
   */
  ownerName?: string | null;
}

const ChatBubble = React.forwardRef<HTMLDivElement, ChatBubbleProps>(
  ({ message, timezone, highlightType, dimmed, ownerName }, ref) => {
    const isLead = message.sender === "lead";
    const isSystem = message.sender === "system";

    if (isSystem) {
      return (
        <div className="flex justify-center my-2">
          <span className="token-11 text-glass-muted italic">{message.text}</span>
        </div>
      );
    }

    const ringClass =
      highlightType === "highlight"
        ? "ring-2 ring-amber-400 ring-offset-2 animate-pulse-once"
        : highlightType === "anchor"
          ? "ring-2 ring-amber-200 ring-offset-2"
          : "";

    const displayText = message.text || (
      <span className="italic opacity-60">
        {message.has_media ? "[Image sent]" : isLead ? "[Story reaction]" : "[ManyChat automation]"}
      </span>
    );

    return (
      <div
        ref={ref}
        className={`flex items-end gap-1.5 ${isLead ? "justify-start" : "justify-end"} mb-2 ${dimmed ? "opacity-50" : ""}`}
      >
        <div
          className={`max-w-[80%] px-3 py-2 rounded-2xl ${ringClass} ${
            isLead
              ? "bg-black/[0.05] text-glass rounded-bl-none"
              : dimmed
                ? "bg-blue-500/40 text-ds-on-accent rounded-br-none"
                : "bg-blue-500 text-ds-on-accent rounded-br-none"
          }`}
        >
          <p className="token-14 whitespace-pre-wrap break-words">{displayText}</p>
          <p className={`token-10 mt-1 ${isLead ? "text-glass-muted" : "text-blue-100"}`}>
            {formatDateTimeInTZ(message.sent_at, timezone)}
          </p>
        </div>
        {/* owner_at_send badge — who owned the lead when this outbound msg was sent (STEP 0) */}
        {!isLead && ownerName !== undefined && (
          <Tooltip content={ownerName || "Unknown owner"}>
            <span
              data-testid="owner-badge"
              className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center token-9 font-semibold ${getOwnerColor(
                ownerName
              )} ${ownerName ? "text-white" : "text-glass-muted"} ${dimmed ? "opacity-50" : ""}`}
            >
              {getOwnerInitials(ownerName)}
            </span>
          </Tooltip>
        )}
      </div>
    );
  }
);

ChatBubble.displayName = "ChatBubble";

export default ChatBubble;

// =============================================================================
// MINI BUBBLE — Compact chat bubble for grid views
// =============================================================================
// Used by ProofGridView and SessionMessagesModal. Works with raw DB message
// shape (sender_type, message_text) unlike ChatBubble (sender, text).
// Supports highlight rings (proof) and context dimming (session messages).
// =============================================================================

export interface MiniBubbleMessage {
  message_text: string | null;
  sent_at: string;
  sender_type: "lead" | "setter" | string;
  has_media?: boolean;
}

interface MiniBubbleProps {
  message: MiniBubbleMessage;
  timezone: string;
  highlightType?: "highlight" | "anchor";
  isContext?: boolean;
  /** Dim this bubble (outside active date range) */
  dimmed?: boolean;
  /** Show full date+time (default) or time-only */
  showDate?: boolean;
}

export const MiniBubble = React.forwardRef<HTMLDivElement, MiniBubbleProps>(
  ({ message, timezone, highlightType, isContext, dimmed, showDate = true }, ref) => {
    const isLead = message.sender_type === "lead";

    const ringClass =
      highlightType === "highlight"
        ? "ring-2 ring-amber-400 ring-offset-1 animate-pulse-once"
        : highlightType === "anchor"
          ? "ring-2 ring-amber-200 ring-offset-1"
          : "";

    return (
      <div
        ref={ref}
        className={`flex ${isLead ? "justify-start" : "justify-end"} mb-1.5 ${isContext ? "opacity-40" : ""} ${dimmed ? "opacity-50" : ""}`}
        data-testid={
          highlightType === "highlight"
            ? "highlight-bubble"
            : highlightType === "anchor"
              ? "anchor-bubble"
              : undefined
        }
      >
        <div
          className={`max-w-[85%] px-2 py-1.5 rounded-xl ${ringClass} ${
            isLead
              ? "bg-black/[0.05] text-glass rounded-bl-none"
              : dimmed
                ? "bg-blue-500/40 text-ds-on-accent rounded-br-none"
                : "bg-blue-500 text-ds-on-accent rounded-br-none"
          }`}
        >
          <p className="token-chat whitespace-pre-wrap break-words">
            {message.message_text || (
              <span className="italic opacity-60">
                {message.has_media
                  ? "[Image sent]"
                  : isLead
                    ? "[Story reaction]"
                    : "[ManyChat automation]"}
              </span>
            )}
          </p>
          <p className={`token-9 mt-0.5 ${isLead ? "text-glass-muted" : "text-blue-100"}`}>
            {showDate
              ? formatDateTimeInTZ(message.sent_at, timezone)
              : formatTimeInTZ(message.sent_at, timezone)}
          </p>
        </div>
      </div>
    );
  }
);

MiniBubble.displayName = "MiniBubble";
